import React, {useContext, useEffect, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Radio,
    RadioGroup,
    Typography,
} from '@mui/material';
import APIWrapperContext from '../../../../services/APIWrapperContext';
import {InvoiceService, InvoiceEmitter} from '../../../../services/invoiceService';

interface EmitterSelectDialogProps {
    open: boolean;
    onClose: () => void;
    onSelect: (emitter: InvoiceEmitter) => void;
}

const EmitterSelectDialog: React.FC<EmitterSelectDialogProps> = ({open, onClose, onSelect}) => {
    const {call_backend_api} = useContext(APIWrapperContext);
    const [emitters, setEmitters] = useState<InvoiceEmitter[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedId, setSelectedId] = useState<number | null>(null);

    useEffect(() => {
        if (open) {
            loadEmitters();
        }
    }, [open]);

    const loadEmitters = () => {
        setLoading(true);
        setError(null);
        call_backend_api(
            InvoiceService.getEmitters(true),
            (data: InvoiceEmitter[]) => {
                setEmitters(data);
                setLoading(false);
                // Auto-select if only one emitter
                if (data.length === 1) {
                    setSelectedId(data[0].id);
                }
            },
            (err: any) => {
                console.error('Error loading emitters:', err);
                setError('Fehler beim Laden der Rechnungsaussteller');
                setLoading(false);
            },
            undefined,
            false
        );
    };

    const handleContinue = () => {
        const selected = emitters.find(e => e.id === selectedId);
        if (selected) {
            onSelect(selected);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Rechnungsaussteller auswählen</DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Alert severity="error" sx={{mt: 1}}>
                        {error}
                    </Alert>
                ) : emitters.length === 0 ? (
                    <Alert severity="info" sx={{mt: 1}}>
                        Keine Rechnungsaussteller vorhanden. Bitte erstellen Sie zuerst einen Rechnungsaussteller in den Einstellungen.
                    </Alert>
                ) : (
                    <Box sx={{mt: 1}}>
                        <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                            Wählen Sie den Rechnungsaussteller für diese Rechnung:
                        </Typography>
                        <RadioGroup
                            value={selectedId?.toString() || ''}
                            onChange={(e) => setSelectedId(parseInt(e.target.value))}
                        >
                            {emitters.map((emitter) => (
                                <FormControlLabel
                                    key={emitter.id}
                                    value={emitter.id.toString()}
                                    control={<Radio />}
                                    label={
                                        <Box>
                                            <Typography variant="body1" fontWeight="medium">
                                                {emitter.name}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {emitter.street}, {emitter.postal_code} {emitter.city}
                                            </Typography>
                                        </Box>
                                    }
                                    sx={{
                                        mb: 1,
                                        p: 1,
                                        borderRadius: 1,
                                        '&:hover': {backgroundColor: 'action.hover'},
                                    }}
                                />
                            ))}
                        </RadioGroup>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Abbrechen</Button>
                <Button
                    onClick={handleContinue}
                    variant="contained"
                    disabled={!selectedId || loading || emitters.length === 0}
                >
                    Weiter
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EmitterSelectDialog;
